import React, { useContext, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ConfirmDialog } from "primereact/confirmdialog";
import useAPIRequest from "../../../custom_hooks/simple/useAPIRequest";
import UiContext from "../../../store/ui-context";
import VerifAuthorizationDialog from "../../../dialogs/VerifAuthorizationDialog";
import useEditorState from "../hooks/page/useEditorState";
import useEditorVerifSetting from "../hooks/signature/useEditorVerifSetting";
import useEditorHeaderApi from "../hooks/header/useEditorHeaderApi";
import useEditorDetilApi from "../hooks/checklist/useEditorDetilApi";
import useEditorImageApi from "../hooks/foto/useEditorImageApi";
import useEditorVerify from "../hooks/signature/useEditorVerify";
import useEditorInitialLoad, {
  useEditorKendaraanFromUrl,
} from "../hooks/page/useEditorInitialLoad";
import useEditorTitle from "../hooks/page/useEditorTitle";
import useEditorPermission from "../hooks/page/useEditorPermission";
import useEditorSelectorDialogs from "../hooks/header/useEditorSelectorDialogs";
import useEditorCamera from "../hooks/foto/useEditorCamera";
import useEditorMarking from "../hooks/foto/useEditorMarking";
import useEditorSignature from "../hooks/signature/useEditorSignature";
import useEditorNote from "../hooks/checklist/useEditorNote";
import useEditorNavigation from "../hooks/page/useEditorNavigation";
import useEditorFooterActions from "../hooks/page/useEditorFooterActions";
import EditorPopover from "./checklist/EditorPopover";
import EditorNoteDialog from "./checklist/EditorNoteDialog";
import EditorFotoUploader from "./checklist/EditorFotoUploader";
import EditorSelectorDialogs from "./dialogs/EditorSelectorDialogs";
import EditorHeaderCard from "./header/EditorHeaderCard";
import EditorActionBar from "./actions/EditorActionBar";
import EditorSectionHeader from "./common/EditorSectionHeader";
import EditorChecklistSection from "./checklist/EditorChecklistSection";
import EditorFotoKendaraanSection from "./foto/EditorFotoKendaraanSection";
import EditorSignatureSection from "./signature/EditorSignatureSection";
import EditorFooterActions from "./actions/EditorFooterActions";
import { getFotoFolderName } from "../utils/editorHelpers";

/**
 * The audit editor page shared by the mobil and the trado audit.
 *
 * `variant` is everything that differs between the two (see audit-kendaraan-mobil-editor / audit-kendaraan-trado-editor):
 *   config   values read by the shared hooks and components (see auditEditorConfig.js):
 *     baseTitle                title of the page
 *     requireTrailer           a trailer is needed when there is no kendaraan
 *     showTrailerField         the trailer field is shown in the header
 *     kendaraanSelectorFilter  which kendaraan can be picked
 */
const EditorMain = ({ variant }) => {
  const { config } = variant;

  const { id } = useParams();
  const uiContext = useContext(UiContext);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const currentUrl = location.pathname;
  const employee = useSelector((state) => state.auth.employee);
  const { requestGet, requestGetOne, requestPost, requestDelete, loading } =
    useAPIRequest();
  const permissions = useSelector((state) => state.auth.permissions);

  const [isPnjTransport, setIsPnjTransport] = useState(
    permissions.includes("view pnj-transport")
  );

  // shared dependencies handed to every hook
  const ctx = {
    id,
    uiContext,
    navigate,
    dispatch,
    location,
    currentUrl,
    employee,
    permissions,
    requestGet,
    requestGetOne,
    requestPost,
    requestDelete,
  };

  // data state
  const state = useEditorState();
  const {
    headerData,
    headerError,
    setHeaderData,
    pengecekanData,
    pengecekanDetilData,
    setPengecekanDetilData,
    listProblemData,
  } = state;

  // API call
  const verifSetting = useEditorVerifSetting(ctx);
  const { verifSettingPengecekan } = verifSetting;
  const headerApi = useEditorHeaderApi({ ...ctx, ...state, ...config });
  const detilApi = useEditorDetilApi({ ...ctx, ...state });
  const imageApi = useEditorImageApi({ ...ctx, ...state, ...detilApi });
  const verifyApi = useEditorVerify({ ...ctx });

  // first time initializations
  useEditorInitialLoad({ ...ctx, ...state, ...headerApi });
  useEditorTitle({ ...ctx, ...state, ...config });
  useEditorKendaraanFromUrl({ ...ctx, ...headerApi });
  const { permittedToEdit } = useEditorPermission({ ...ctx, ...state });

  // handle UI event section
  const selectorDialogs = useEditorSelectorDialogs({ ...state });
  const camera = useEditorCamera({ ...imageApi });
  const marking = useEditorMarking({ ...state, ...detilApi });
  const signature = useEditorSignature({ ...ctx, ...state, ...verifyApi });
  const note = useEditorNote({ permittedToEdit, ...detilApi });
  const navigation = useEditorNavigation({ ...ctx, ...state, ...headerApi });
  const footerActions = useEditorFooterActions({ ...ctx, ...state });

  const { handleOnClickReload } = navigation;
  const folderName = getFotoFolderName(headerData);

  // props shared by the checklist and the photo sections
  const sectionProps = {
    listProblemData,
    headerData,
    permittedToEdit,
    loading,
    folderName,
    pengecekanData,
    pengecekanDetilData,
    setPengecekanDetilData,
    handleOnClickReload,
  };

  // render UI section
  return (
    <div className={`col-start-start px-4 gap-4 w-100`}>
      <ConfirmDialog />
      <VerifAuthorizationDialog loading={loading} />
      <div></div>
      <EditorPopover
        overlayRef={note.overlayRef}
        popoverPengecekanData={note.popoverPengecekanData}
        handleOnClickFoto={note.handleOnClickFoto}
        handleOnClickEditNote={note.handleOnClickEditNote}
      />
      <EditorNoteDialog {...note} folderName={folderName} />
      <EditorFotoUploader
        uploaderRef={note.fotoUploaderRef}
        fotoTargetRef={note.fotoTargetRef}
        headerData={headerData}
        pengecekanDetilData={pengecekanDetilData}
        onChangeFoto={note.handleOnChangeFotoDetil}
      />
      <EditorSelectorDialogs
        headerData={headerData}
        loading={loading}
        kendaraanSelectorFilter={config.kendaraanSelectorFilter}
        {...marking}
        {...selectorDialogs}
      />
      {/* hidden native camera/gallery input for the vehicle photos (tampak depan/kiri/kanan/belakang) */}
      <input
        ref={camera.cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: "none" }}
        onChange={camera.handleCameraFileChange}
      />
      <EditorHeaderCard
        headerData={headerData}
        setHeaderData={setHeaderData}
        headerError={headerError}
        permittedToEdit={permittedToEdit}
        loading={loading}
        showTrailerField={config.showTrailerField}
        handleOnClickKendaraan={selectorDialogs.handleOnClickKendaraan}
        handleOnClickTrailer={selectorDialogs.handleOnClickTrailer}
      />
      <EditorActionBar
        headerData={headerData}
        loading={loading}
        permittedToEdit={permittedToEdit}
        isPnjTransport={isPnjTransport}
        handleOnClickBack={navigation.handleOnClickBack}
        handleOnClickSaveHeader={navigation.handleOnClickSaveHeader}
        handleOnClickReload={handleOnClickReload}
      />

      <div className="col-start-start gap-4 w-full" id="excludeFromPrint">
        <EditorSectionHeader title="CHECKLIST" />
        <EditorChecklistSection
          {...sectionProps}
          handleOnClickDeskripsi={note.handleOnClickDeskripsi}
          handleOnClickSaveCheckList={detilApi.handleOnClickSaveCheckList}
        />
        <EditorSectionHeader title="FOTO KENDARAAN" />
        <EditorFotoKendaraanSection
          {...sectionProps}
          handleOnClickCameraPreview={camera.handleOnClickCameraPreview}
          handleOnClickAddMarking={marking.handleOnClickAddMarking}
          handleOnChangeKordinat={marking.handleOnChangeKordinat}
          handleOnClickRemoveMarking={marking.handleOnClickRemoveMarking}
          handleOnClickSaveMarkingList={detilApi.handleOnClickSaveMarkingList}
        />
      </div>

      {/* SIGNATURE */}
      <EditorSignatureSection
        headerData={headerData}
        verifSettingPengecekan={verifSettingPengecekan}
        {...signature}
      />

      <EditorFooterActions loading={loading} {...footerActions} />

      <div className="h-[3rem]"></div>
    </div>
  );
};

export default EditorMain;
